import React, {useEffect, useMemo, useCallback, useState} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {
  Input,
  Fieldset,
  Select,
  SelectOption,
  Heading,
  Text,
  Infobox
} from '@flixbus/honeycomb-react';
import {IconWarning} from '@flixbus/honeycomb-icons-react';
import {yupResolver} from '@hookform/resolvers/yup';
import ContentLoader from '@/Pages/Common/ContentLoader.jsx';
import CpcPanel from '@/Pages/Common/CpcPanel.jsx';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {confirm} from '@/Pages/Common/Confirm/index.js';
import {useSaveCountry} from '../../hooks/useSaveCountry.ts';
import {initCountryObject} from '../../store/data.ts';
import CountryCodesAutocomplete from '../CountryCodesAutocomplete/CountryCodesAutocomplete';
import CountryNameAutocomplete from '../CountryNameAutocomplete/CountryNameAutocomplete.js';
import {getValueDimensions} from '../index.constants.ts';
import {errorMessages, countryPanelSchema} from './CountryPanel.constants.tsx';
import {
  CountryPanelFormData,
  CountryPanelProps
} from './CountryPanel.types.tsx';
import {useCountryCurrencies} from '../../hooks/useCountryCurrencies.ts';
import {Country} from '../../store/types.ts';

const CountryPanel: React.FC<CountryPanelProps> = ({
  initialCountry,
  onClose
}) => {
  const {saveCountry} = useSaveCountry();
  const {localUnits} = useLocalUnitsContext();
  const isEditCountry = Boolean(initialCountry?.id);

  const [error, setError] = useState<string | null>(null);

  const {
    control,
    watch,
    setValue,
    formState: {errors},
    handleSubmit,
    reset
  } = useForm<CountryPanelFormData>({
    resolver: yupResolver(countryPanelSchema),
    defaultValues: initCountryObject
  });

  const {currencies} = useCountryCurrencies();
  const currencyOptions = useMemo(
    () =>
      currencies
        ? currencies.map(currency => (
            <SelectOption key={currency} value={currency}>
              {currency}
            </SelectOption>
          ))
        : [],
    [currencies]
  );

  const currency = watch('currency');

  useEffect(() => {
    if (initialCountry) {
      Object.entries(initialCountry).forEach(([key, val]) =>
        setValue(key as keyof CountryPanelFormData, val)
      );
    }
  }, [initialCountry, setValue]);

  const costDimensionOptions = useMemo(
    () =>
      getValueDimensions(currency, localUnits).map(({key, value}) => (
        <SelectOption key={key} value={key}>
          {value}
        </SelectOption>
      )),
    [currency, localUnits]
  );

  const handleClose = useCallback(
    (country?: Country) => {
      onClose(country);
      reset();
      setError(null);
    },
    [onClose, reset]
  );

  const onSubmit = useCallback(
    async (country: CountryPanelFormData) => {
      if (isEditCountry && initialCountry?.currency !== country.currency) {
        const confirmed = await confirm({
          PopupIcon: IconWarning,
          title:
            'Currency has been edited. Be aware that Regional and Bus Partner cost values might need to be adjusted to match the new currency. Do you wish to proceed with the changes?',
          message: `${country.name}`
        });
        if (!confirmed) return;
      }

      const trimmedName = country.name.trim();
      // Update the form field with trimmed value
      setValue('name', trimmedName, {shouldValidate: true, shouldDirty: true});

      saveCountry({...country, name: trimmedName}, handleClose, setError);
    },
    [
      initialCountry?.currency,
      isEditCountry,
      setValue,
      saveCountry,
      handleClose
    ]
  );

  const countryCodesError = useMemo(() => {
    const countryCodeErrors: {message?: string}[] = Array.isArray(
      errors.countryCodes
    )
      ? errors.countryCodes
      : [];

    const firstError = countryCodeErrors.find(err => err?.message);

    const errorMessage: React.ReactNode | undefined = firstError?.message
      ? errorMessages[firstError.message as keyof typeof errorMessages]
      : undefined;

    return {
      valid: !firstError,
      errorMessage
    };
  }, [errors.countryCodes]);

  return (
    <CpcPanel
      title={isEditCountry ? 'Edit country' : 'Add country'}
      activeAction={Boolean(initialCountry)}
      closeAction={() => handleClose()}
      saveAction={handleSubmit(onSubmit)}
    >
      {initialCountry ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Infobox
              aria-label="Some critical error occurred"
              appearance="danger"
            >
              <Text>{error}</Text>
            </Infobox>
          )}

          <Fieldset>
            <Controller
              name="name"
              control={control}
              render={({field}) => (
                <CountryNameAutocomplete
                  countryName={field.value}
                  onSelected={field.onChange}
                  valid={!errors.name}
                  errorMessage={
                    errors.name?.message && errorMessages[errors.name.message]
                  }
                />
              )}
            />

            <Controller
              name="businessRegion"
              control={control}
              render={({field}) => (
                <Input
                  id="country-business-region"
                  name="businessRegion"
                  label="Business region"
                  value={field.value}
                  onChange={field.onChange}
                  valid={!errors.businessRegion}
                />
              )}
            />

            <Controller
              name="currency"
              control={control}
              render={({field}) => (
                <Select
                  id="country-select-currency"
                  label="Currency"
                  placeholder="Select a Currency"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  valid={!errors.currency}
                >
                  {currencyOptions}
                </Select>
              )}
            />

            <Controller
              name="countryCodes"
              control={control}
              render={({field}) => (
                <CountryCodesAutocomplete
                  countryCodes={field.value}
                  onSelected={field.onChange}
                  valid={countryCodesError.valid}
                  errorMessage={countryCodesError.errorMessage}
                />
              )}
            />
          </Fieldset>

          <Heading key={4} size={4}>
            Measuring units
          </Heading>
          <Text
            extraClasses="hcr-space-5-bottom grayscale-70-color"
            style={{whiteSpace: 'nowrap'}}
            small
          >
            Specify the measuring unit for “Service &
            <br />
            Maintenance” and “Damage repair” on country level:
          </Text>

          <Fieldset>
            <Controller
              name="repairCostDimension"
              control={control}
              render={({field}) => (
                <Select
                  id="country-select-repair-cost-dimension"
                  label="Damage repair"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  valid={!errors.repairCostDimension}
                >
                  {costDimensionOptions}
                </Select>
              )}
            />

            <Controller
              name="maintenanceCostDimension"
              control={control}
              render={({field}) => (
                <Select
                  id="country-select-maintenance-cost-dimension"
                  label="Service & Maintenance"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  valid={!errors.maintenanceCostDimension}
                >
                  {costDimensionOptions}
                </Select>
              )}
            />
          </Fieldset>
        </form>
      ) : (
        <ContentLoader />
      )}
    </CpcPanel>
  );
};

export default React.memo(CountryPanel);
